import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { resetPassword } from "../../services/auth.services";
import { showSnackBar } from "../../store/common/snackBarSlice";
import InputField from "../../Components/Common/Form/InputField";
import { useTranslation } from "react-i18next";

const EnvelopeIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const LockIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 11c-1.104 0-2-.896-2-2V6c0-1.104.896-2 2-2s2 .896 2 2v3c0 1.104-.896 2-2 2zm3 0h1c1.104 0 2 .896 2 2v7c0 1.104-.896 2-2 2H8c-1.104 0-2-.896-2-2v-7c0-1.104.896-2 2-2h1"
    />
  </svg>
);

const CodeIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 20h4M12 4v16m8-8H4"
    />
  </svg>
);

const BackIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 19l-7-7m0 0l7-7m-7 7h18"
    />
  </svg>
);

const BtnLoader = () => (
  <div className="flex justify-center items-center">
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
  </div>
);

const ResetPassword = () => {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: "",
    code: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const emailRef = useRef(null);
  const codeRef = useRef(null);
  const passwordRef = useRef(null);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      validateSingleField(field, value);
    }
  };

  const validateSingleField = (field, value) => {
    let isValid = false;
    switch (field) {
      case "email":
        isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        break;
      case "code":
        isValid = value && value.trim() !== "";
        break;
      case "password":
        isValid = value && value.length >= 8;
        break;
      default:
        isValid = value && value.trim() !== "";
    }
    if (isValid) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email || formData.email.trim() === "") {
      newErrors.email = t("validation.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("validation.emailInvalid");
    }
    if (!formData.code || formData.code.trim() === "") {
      newErrors.code = t("validation.codeRequired");
    }
    if (!formData.password || formData.password.trim() === "") {
      newErrors.password = t("validation.passwordRequired");
    } else if (formData.password.length < 8) {
      newErrors.password = t("validation.passwordMin");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validateForm();
    if (!isValid) {
      scrollToFirstError();
      return;
    }
    try {
      setLoading(true);
      const res = await resetPassword(formData);
      if (res.error) {
        dispatch(
          showSnackBar({
            text: res?.error?.response?.data?.message ?? t("common:messages.somethingWentWrong"),
            severity: "error",
          })
        );
      } else {
        dispatch(
          showSnackBar({
            text: t("reset.done"),
            severity: "success",
          })
        );
        navigate("/login");
      }
    } catch (error) {
      dispatch(
        showSnackBar({
          text: error?.message || t("common:messages.somethingWentWrong"),
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const scrollToFirstError = () => {
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) return;
    const fieldRefs = {
      email: emailRef,
      code: codeRef,
      password: passwordRef,
    };
    const firstErrorField = errorFields.find((field) => errors[field]);
    if (firstErrorField && fieldRefs[firstErrorField]?.current) {
      fieldRefs[firstErrorField].current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      if (fieldRefs[firstErrorField].current.focus) {
        fieldRefs[firstErrorField].current.focus();
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-light to-ui-page flex items-center justify-center py-4 px-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <div className="mb-2">
          <button
            onClick={() => navigate("/login")}
            className="inline-flex items-center gap-1 text-sm font-medium text-white bg-brand hover:bg-brand-hover px-3 py-1.5 rounded-md shadow-sm transition-colors duration-200"
          >
            <BackIcon />
            {t("reset.backToLogin")}
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-ui-surface rounded-xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-brand px-4 py-3 text-center">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-white/20 rounded-full mb-1">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
            </div>
            <h1 className="text-lg font-bold text-white">{t("reset.title")}</h1>
            <p className="text-xs text-white/80 mt-1">
              {t("reset.intro")}
            </p>
          </div>

          {/* Content */}
          <div className="p-4">
            <form onSubmit={handleSubmit} className="space-y-3">
              <InputField
                label={t("reset.emailLabel")}
                type="email"
                value={formData.email}
                ref={emailRef}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                error={errors.email}
                placeholder={t("reset.emailPlaceholder")}
                icon={<EnvelopeIcon />}
                required
              />
              <InputField
                label={t("reset.codeLabel")}
                type="text"
                value={formData.code}
                ref={codeRef}
                onChange={(e) => handleFieldChange("code", e.target.value)}
                error={errors.code}
                placeholder={t("reset.codePlaceholder")}
                icon={<CodeIcon />}
                required
              />
              <InputField
                label={t("reset.newPasswordLabel")}
                type="password"
                value={formData.password}
                ref={passwordRef}
                onChange={(e) => handleFieldChange("password", e.target.value)}
                error={errors.password}
                placeholder={t("reset.newPasswordPlaceholder")}
                icon={<LockIcon />}
                required
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2.5 px-4 rounded-lg transition-all duration-300 ease-out hover:shadow-lg active:scale-[0.98] shadow-md"
              >
                {loading ? <BtnLoader /> : t("reset.submit")}
              </button>
              <div className="text-center pt-2 border-t border-ui-surface-sunken">
                <p className="text-sm text-ui-text-muted">
                  {t("login.noAccount")}{" "}
                  <Link
                    to="/register"
                    className="font-semibold text-brand hover:text-brand-hover transition-colors duration-200"
                  >
                    {t("login.registerHere")}
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-ui-text-muted mt-3">
          {t("login.needHelp")}{" "}
          <a
            href="mailto:info@hajexbolt.com"
            className="text-brand hover:text-brand-hover font-medium transition-colors duration-200"
          >
            {t("login.contactSupport")}
          </a>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
